import { useState, useEffect, useCallback, useRef } from 'react';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import type { AuthUser } from 'aws-amplify/auth';
import { useCognitoContext } from '../providers/cognito-provider';

interface UseCognitoAuthReturn {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  signInWithRedirect: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export const useCognitoAuth = (): UseCognitoAuthReturn => {
  const { isConfigured } = useCognitoContext();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasCheckedAuth = useRef(false);

  const checkAuth = useCallback(async (isOAuthCallback = false) => {
    try {
      setIsLoading(true);

      if (isOAuthCallback) {
        try {
          await fetchAuthSession({ forceRefresh: true });
        } catch (sessionErr) {
          // Ignore session errors during OAuth callback - getCurrentUser will handle it
        }
      }

      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setIsAuthenticated(true);
      setError(null);
    } catch (err) {
      setUser(null);
      setIsAuthenticated(false);
      if (err instanceof Error) {
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isConfigured) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorParam = urlParams.get('error');

    if (code || errorParam) {
      hasCheckedAuth.current = true;
      checkAuth(true).then(() => {
        setTimeout(() => {
          if (window.history.replaceState) {
            const cleanUrl = window.location.pathname || '/';
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }, 50);
      });
    } else if (!hasCheckedAuth.current) {
      hasCheckedAuth.current = true;
      checkAuth(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigured]);

  const redirectToSignIn = useCallback(async () => {
    try {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
          setError(null);
          return;
        }
      } catch {
        // User is not authenticated, proceed with redirect
      }

      await signInWithRedirect();
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'name' in err &&
        err.name === 'UserAlreadyAuthenticatedException'
      ) {
        try {
          const currentUser = await getCurrentUser();
          setUser(currentUser);
          setIsAuthenticated(true);
          setError(null);
        } catch (getUserErr) {
          if (getUserErr instanceof Error) {
            setError(getUserErr);
          }
        }
        return;
      }

      if (err instanceof Error) {
        setError(err);
      }
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await signOut({ global: true });
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      if (err instanceof Error) {
        setError(err);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      const idToken = session.tokens?.idToken;
      if (!idToken) {
        return null;
      }

      let tokenString: string | null = null;

      if (typeof idToken.toString === 'function') {
        tokenString = idToken.toString();
      }

      if (!tokenString || tokenString === '[object Object]' || tokenString.length < 50) {
        tokenString = String(idToken);
      }

      if (
        (!tokenString || tokenString === '[object Object]') &&
        idToken &&
        typeof (idToken as unknown as { token?: string }).token === 'string'
      ) {
        tokenString = (idToken as unknown as { token: string }).token;
      }

      if (!tokenString || !tokenString.startsWith('eyJ') || tokenString.length < 100) {
        return null;
      }

      return tokenString;
    } catch (err) {
      return null;
    }
  }, []);

  return {
    user,
    isLoading,
    isAuthenticated,
    error,
    signInWithRedirect: redirectToSignIn,
    logout,
    getAccessToken,
  };
};
